import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Eye, FileText, AlertCircle, CheckCircle } from 'lucide-react';

const VehicleInspectionList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchInspections();
    fetchVehicles();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles');
    }
  };

  const handleView = async (inspectionId) => {
    try {
      const response = await axios.get(`${API}/vehicle-inspections/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInspection(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load inspection details');
    }
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `#${vehicle.vehicle_number} - ${vehicle.tag}` : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-800',
      'Needs Follow-Up': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const countIssues = (inspection) => {
    let count = 0;
    const allItems = [
      ...inspection.exterior_items,
      ...inspection.interior_items,
      ...inspection.fluids_items,
      ...inspection.safety_equipment_items,
      ...inspection.mechanical_items
    ];
    allItems.forEach(item => {
      if (item.status === 'needs_attention') count++;
    });
    return count;
  };

  const formatDate = (dateString) => {
    return _fmtDate(dateString);
  };

  const renderInspectionItems = (title, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 className="font-semibold text-sm mb-2 text-slate-700">{title}</h4>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div 
              key={index} 
              className={`p-3 rounded border ${
                item.status === 'needs_attention' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <p className="text-sm font-medium flex-1">{item.item}</p>
                {item.status === 'needs_attention' ? (
                  <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0 ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                )}
              </div>
              {item.status === 'needs_attention' && item.description && (
                <p className="text-xs text-slate-600 mt-2 pl-2 border-l-2 border-orange-400">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Vehicle Inspections / Inspecciones de Vehículos
          </h2>
          <p className="text-sm text-slate-600">Weekly standardized inspections / Inspecciones estandarizadas semanales</p>
        </div>
        <Button
          onClick={() => navigate('/field/vehicle-inspection/new')}
          style={{ backgroundColor: '#6B9F3E' }}
          data-testid="create-inspection-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Inspection
        </Button>
      </div>

      <div className="space-y-3">
        {inspections.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No inspections yet.</p>
              <p className="text-slate-500 text-sm">Aún no hay inspecciones.</p>
              <Button
                onClick={() => navigate('/field/vehicle-inspection/new')}
                className="mt-4"
                style={{ backgroundColor: '#6B9F3E' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Inspection
              </Button>
            </CardContent>
          </Card>
        ) : (
          inspections.map((inspection) => {
            const issuesCount = countIssues(inspection);
            return (
              <Card 
                key={inspection.id} 
                className="hover:shadow-md transition-shadow"
                style={{ borderLeft: `4px solid ${issuesCount > 0 ? '#F59E0B' : '#10B981'}` }}
                data-testid="inspection-list-item"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-base">{inspection.inspection_number}</CardTitle>
                        {issuesCount > 0 && (
                          <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                            {issuesCount} issue{issuesCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {getVehicleInfo(inspection.vehicle_id)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(inspection.id)}
                      data-testid="view-inspection-btn"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Date / Fecha:</span>
                      <span className="font-medium">{_fmtDate(inspection.date_of_inspection)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Location / Ubicación:</span>
                      <span className="font-medium">{inspection.location_of_inspection}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Status / Estado:</span>
                      {getStatusBadge(inspection.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection Details / Detalles de la Inspección</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-6">
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Inspection Number</Label>
                    <p className="font-bold">{selectedInspection.inspection_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Vehicle / Vehículo</Label>
                    <p className="font-bold">{getVehicleInfo(selectedInspection.vehicle_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date / Fecha</Label>
                    <p className="font-medium">{_fmtDate(selectedInspection.date_of_inspection)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location / Ubicación</Label>
                    <p className="font-medium">{selectedInspection.location_of_inspection}</p>
                  </div>
                </div>
              </div>

              {renderInspectionItems('EXTERIOR', selectedInspection.exterior_items)}
              {renderInspectionItems('INTERIOR', selectedInspection.interior_items)}
              {renderInspectionItems('FLUIDS / FLUIDOS', selectedInspection.fluids_items)}
              {renderInspectionItems('SAFETY EQUIPMENT / EQUIPO DE SEGURIDAD', selectedInspection.safety_equipment_items)}
              {renderInspectionItems('MECHANICAL / MECÁNICO', selectedInspection.mechanical_items)}

              {selectedInspection.additional_notes && (
                <div>
                  <Label className="text-xs text-slate-500">Additional Notes / Notas Adicionales</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded">{selectedInspection.additional_notes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Driver Signature / Firma del Conductor</Label>
                  <p className="font-medium italic">{selectedInspection.driver_signature}</p>
                </div>
                {selectedInspection.supervisor_signature && (
                  <div>
                    <Label className="text-xs text-slate-500">Supervisor Signature / Firma del Supervisor</Label>
                    <p className="font-medium italic">{selectedInspection.supervisor_signature}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-500">Status / Estado</Label>
                <div className="mt-1">{getStatusBadge(selectedInspection.status)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleInspectionList;